import React from "react";
import { FlowConfiguration } from "../../rule-engine-core/Flow/FlowConfiguration";
import { Droppable, DroppableProvided, DroppableStateSnapshot } from "react-beautiful-dnd";
import ReactDraggable, { DraggableData, DraggableEvent } from "react-draggable";
import styled from "styled-components";

import { FlowStepSequence } from "../../rule-engine-core/Flow/Structure/FlowStepSequence";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { IPosition } from "./Common/IPosition";
import { FlowEditContext } from "../FlowEditContext";
import MovementSvg from "../../assets/images/icons/movement_white.svg";
import { FlowStepOutputInstructionType } from "../../rule-engine-core/Flow/Structure/FlowStepOutputInstructions";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { FlowConstants } from "../../rule-engine-core/Flow/Utilities/FlowConstants";
import { RenderSequenceStrategy } from "./CommonRender/RenderSequenceStrategy";
import { RenderBranchSequenceContent, RenderSequenceContent } from "./CommonRender/RenderSequenceContent";
import { RenderSequenceActions } from "./CommonRender/RenderSequenceActions";
import { RenderSequenceTitle } from "./CommonRender/RenderSequenceTitle";
import { Box, Link } from "@mui/material";
import { FlowStepSequenceType } from "../../rule-engine-core/Flow/Structure/FlowStepSequenceType";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { RenderCollapsedStack } from "./CommonRender/RenderCollapsedStack";
import { ExpressionIcon } from "../../CommonUI/Icon/ExpressionIcon";
import { ExpressionEditLayer } from "../../Expressions/ExpressionEditLayer";

const FlowSequenceDiv = styled("div")<{
    position: IPosition;
    padding: string;
    size: { width: number; height?: number };
    isDragging: boolean;
    isDisabledDragging?: boolean;
    selected: boolean;
    borderColor: string;
    background: string;
}>`
    ${(props) =>
        !props.isDisabledDragging &&
        `
            top: ${props.isDragging ? 0 : props.position.y}px;
            left: ${props.isDragging ? 0 : props.position.x}px;
            position: absolute;
	    `}

    margin-bottom: 10px;
    padding: ${(props) => props.padding};
    background: transparent;

    box-sizing: border-box;
    border-radius: 7px;
    min-width: ${(props) => props.size.width}px;

    ${(props) => `border: 2px solid ${props.borderColor};`}
    ${(props) => (!props.isDragging ? `transform: none !important;` : `z-index: 1;`)}
  > div.stack {
        border-radius: 4px;
        position: relative;
        padding: 8px 12px 6px;
        background: ${(props) => props.background};

        ${(props) =>
            !props.isDisabledDragging &&
            props.isDragging &&
            `
			&.stack-move {
				cursor: url(${MovementSvg}), auto;
			}
		`}
    }

    .stack-actions {
        position: absolute;
        right: 5px;
        top: -40px;
        padding-bottom: 20px;

        .button-group {
            opacity: ${(props) => (props.selected ? 1 : 0)};
            transition: opacity 0.1s;

            border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
            box-sizing: border-box;
            border-radius: 4px;
            display: flex;
        }
    }

    &:hover .stack-actions .button-group {
        opacity: 1;
    }
`;

interface IFlowSequenceStack {
    flow: FlowConfiguration;
    sequence: FlowStepSequence;
    editContext: FlowEditContext;
}

export class FlowSequenceStack extends React.Component<
    IFlowSequenceStack,
    { isDragging: boolean; openExpression: boolean }
> {
    containerRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

    constructor(props: IFlowSequenceStack) {
        super(props);

        this.state = {
            isDragging: false,
            openExpression: false,
        };
    }

    static getEndSequenceTitleStyle(sequence: FlowStepSequence) {
        if (sequence.isCollapsed) {
            return {
                color: FlowConstants.END_COLLAPSED_TITLE_COLOR,
            };
        }
        return {
            color: FlowConstants.END_TITLE_COLOR,
        };
    }

    static getPrimarySequenceTitleStyle() {
        return {
            color: FlowConstants.PRIMARY_TITLE_COLOR,
        };
    }

    static getErrorSequenceTitleStyle(sequence: FlowStepSequence) {
        if (sequence.isCollapsed) {
            return {
                color: FlowConstants.ERROR_COLLAPSED_TITLE_COLOR,
            };
        }
        return {
            color: FlowConstants.ERROR_TITLE_COLOR,
        };
    }

    static getSequenceTitleStyle(sequence: FlowStepSequence) {
        // error stack common styles
        if (sequence.isError) return this.getErrorSequenceTitleStyle(sequence);
        // end stack common styles
        if (sequence.isEnd) return this.getEndSequenceTitleStyle(sequence);
        //primary stack common styles
        if (sequence.isPrimary) return this.getPrimarySequenceTitleStyle();

        return {
            color: FlowConstants.DEFAULT_TITLE_COLOR,
        };
    }

    getDefaultPosition = (): IPosition => {
        return {
            x: this.props.sequence.x,
            y: this.props.editContext.getSequenceY(this.props.sequence),
        };
    };

    dragDropAction = (data: DraggableData, isCombining: boolean = false) => {
        this.props.editContext.dragOverSequence = null;

        this.props.sequence.desiredX = data.x;
        this.props.sequence.desiredY = data.y;

        if (!isCombining) {
            StateManager.propertyChanged(this.props.sequence, FlowStepSequence.MOVED);
        }

        this.setState({
            isDragging: false,
        });
    };

    onDragStop = (_e: DraggableEvent, data: DraggableData) => {
        let sequence = this.props.editContext.getSequenceByXY(data.x, data.y);
        if (sequence) {
            this.props.editContext.dragOverSequence = sequence;
            this.props.editContext.notification = {
                message: "Do you really want to combine these stacks?",
                onSuccess: () => {
                    this.props.editContext.notification = undefined;
                    this.dragDropAction(data, true);
                    this.props.editContext.combineSequences(this.props.sequence, sequence!);
                },
                onCancel: () => {
                    this.props.editContext.notification = undefined;
                    this.dragDropAction(data);
                },
            };
        } else {
            this.props.editContext.dragOverSequence = null;
            this.dragDropAction(data);
        }

        StateManager.propertyChanged(this.props.sequence, FlowStepSequence.DRAG_END);
    };

    // eslint-disable-next-line
    onDragStart = (_e: DraggableEvent, _data: DraggableData) => {
        this.props.editContext.clearSelection();
        this.setState({
            isDragging: true,
        });
        StateManager.propertyChanged(this.props.sequence, FlowStepSequence.DRAG_START);
    };

    onDrag = (_e: DraggableEvent, data: DraggableData) => {
        this.props.sequence.desiredX = data.x;
        this.props.sequence.desiredY = data.y;

        let sequence = this.props.editContext.getSequenceByXY(data.x, data.y);
        if (sequence) {
            this.props.editContext.dragOverSequence = sequence;
        } else {
            this.props.editContext.dragOverSequence = null;
        }

        StateManager.propertyChanged(this.props.sequence, FlowStepSequence.DRAG_OVER);
    };

    onSelectSequence = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();

        // end / error stacks can not be selected and skip when reordering the steps.
        let isReordering = Reflect.apply(Reflect.get(e.target, "getAttribute"), e.target, ["data-rbd-droppable-id"]);
        if (this.props.sequence.isNormal && !isReordering) {
            this.props.editContext.setSelection(this.props.sequence);
        }
    };

    onMouseDown = () => {
        if (this.props.sequence.isError) {
            this.props.editContext.showErrorOrEndConnections = FlowStepOutputInstructionType.THROW_EXCEPTION;
        } else if (this.props.sequence.isEnd) {
            this.props.editContext.showErrorOrEndConnections = FlowStepOutputInstructionType.END_FLOW;
        }
    };

    onMouseUp = () => {
        if (this.props.sequence.isError || this.props.sequence.isEnd) {
            this.props.editContext.showErrorOrEndConnections = null;
        }
    };

    isEnabledPlaceholderWhileDrop(sequence: FlowStepSequence): boolean {
        // no need animation for collapsed and no steps sequence.
        return sequence.steps.length > 0 && !sequence.isCollapsed;
    }

    getSequenceBorderColor(sequence: FlowStepSequence): string {
        if (this.props.editContext.selection === sequence._id) {
            return FlowConstants.SELECTED_STACK_COLOR;
        }

        if (sequence.getValidationIssues().length) {
            return FlowConstants.ERROR_STACK_COLOR;
        }

        // While step is dragging over on collapsed sequence..
        if (this.props.editContext.isDraggingOverSequence(sequence) && !this.isEnabledPlaceholderWhileDrop(sequence)) {
            return FlowConstants.SSS_STEP_DROP_OVER_BORDER_COLOR;
        }
        return "transparent";
    }

    getSequenceBackgroundColor(sequence: FlowStepSequence): string {
        // background color when candidate or stepsequence is dragging over on this sequence.
        if (this.props.editContext.isDragOver(sequence._id)) {
            return FlowConstants.OVER_SEQUENCE_COLOR;
        }
        return sequence.color;
    }

    render() {
        return (
            <ObserveState
                listenTo={this.props.editContext}
                properties={[FlowEditContext.DRAG_OVER_SEQUENCE]}
                control={() => {
                    return (
                        <ReactDraggable
                            disabled={this.props.sequence.isPrimary || !this.props.sequence.isNormal}
                            defaultPosition={this.getDefaultPosition()}
                            position={this.getDefaultPosition()}
                            onStop={this.onDragStop}
                            onStart={this.onDragStart}
                            onDrag={this.onDrag}
                            handle={`.stack-move`}
                            cancel=".inline-editor"
                            nodeRef={this.containerRef}
                        >
                            {this.props.sequence.sequenceType === FlowStepSequenceType.BRANCH_MERGE
                                ? this.RenderBranchStack()
                                : this.RenderNormalStack()}
                        </ReactDraggable>
                    );
                }}
            />
        );
    }

    private RenderNormalStack() {
        return (
            <FlowSequenceDiv
                id={this.props.sequence.getElementId()}
                padding={"4px"}
                size={this.props.editContext.getSequenceMainSize(this.props.sequence)}
                background={this.getSequenceBackgroundColor(this.props.sequence)}
                position={this.getDefaultPosition()}
                isDragging={this.state.isDragging}
                isDisabledDragging={!this.props.sequence.isNormal}
                selected={this.props.editContext.selection === this.props.sequence._id}
                onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    this.props.editContext.contextControl = {
                        selectedId: this.props.sequence._id,
                        mouseX: e.clientX,
                        mouseY: e.clientY,
                    };
                }}
                onClick={this.onSelectSequence}
                borderColor={this.getSequenceBorderColor(this.props.sequence)}
                ref={this.containerRef}
            >
                <Box
                    className="stack"
                    onMouseDown={this.onMouseDown}
                    onMouseUp={this.onMouseUp}
                    onMouseLeave={this.onMouseUp}
                    display={"flex"}
                >
                    {this.RenderLoopBack()}
                    <Box width={"100%"}>
                        <RenderSequenceTitle sequence={this.props.sequence} />
                        <div className="stack-actions">
                            <div className="button-group">
                                <RenderSequenceActions
                                    editContext={this.props.editContext}
                                    sequence={this.props.sequence}
                                />
                            </div>
                        </div>
                        <Droppable
                            droppableId={this.props.sequence._id}
                            direction={"vertical"}
                        >
                            {(provided: DroppableProvided, _snapshot: DroppableStateSnapshot) => {
                                return (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        // todo: remove
                                        // style={{
                                        //     display:
                                        //         snapshot.isDraggingOver &&
                                        //         this.props.editContext.displayBehavior.isEnabledPlaceholderWhileDrop(
                                        //             this.props.sequence
                                        //         ) &&
                                        //         this.props.editContext.displayBehavior.getSequenceDroppableDirection(
                                        //             this.props.sequence
                                        //         ) === "horizontal"
                                        //             ? "flex"
                                        //             : undefined,
                                        // }}
                                    >
                                        <RenderSequenceContent
                                            sequence={this.props.sequence}
                                            editContext={this.props.editContext}
                                            flow={this.props.flow}
                                            parentColor={this.props.sequence.color}
                                        />
                                        <div
                                            style={{
                                                display: this.isEnabledPlaceholderWhileDrop(this.props.sequence)
                                                    ? "block"
                                                    : "none",
                                            }}
                                        >
                                            {provided.placeholder}
                                        </div>
                                    </div>
                                );
                            }}
                        </Droppable>
                        <ObserveState
                            properties={[FlowStepSequence.STRATEGY]}
                            listenTo={this.props.editContext}
                            control={() => {
                                return (
                                    <RenderSequenceStrategy
                                        editContext={this.props.editContext}
                                        sequence={this.props.sequence}
                                    />
                                );
                            }}
                        />
                    </Box>
                </Box>
            </FlowSequenceDiv>
        );
    }

    private RenderBranchStack() {
        return (
            <FlowSequenceDiv
                id={this.props.sequence.getElementId()}
                padding={"4px"}
                size={this.props.editContext.getSequenceMainSize(this.props.sequence)}
                background={this.getSequenceBackgroundColor(this.props.sequence)}
                position={this.getDefaultPosition()}
                isDragging={this.state.isDragging}
                isDisabledDragging={!this.props.sequence.isNormal}
                selected={this.props.editContext.selection === this.props.sequence._id}
                onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    this.props.editContext.contextControl = {
                        selectedId: this.props.sequence._id,
                        mouseX: e.clientX,
                        mouseY: e.clientY,
                    };
                }}
                onClick={this.onSelectSequence}
                borderColor={this.getSequenceBorderColor(this.props.sequence)}
                ref={this.containerRef}
            >
                <Box
                    className="stack"
                    onMouseDown={this.onMouseDown}
                    onMouseUp={this.onMouseUp}
                    onMouseLeave={this.onMouseUp}
                    display={"flex"}
                    flexDirection={"column"}
                >
                    {this.props.sequence.isCollapsed && (
                        <>
                            <RenderSequenceTitle sequence={this.props.sequence} />
                            <div className="stack-actions">
                                <div className="button-group">
                                    <RenderSequenceActions
                                        editContext={this.props.editContext}
                                        sequence={this.props.sequence}
                                    />
                                </div>
                            </div>

                            <RenderCollapsedStack
                                sequence={this.props.sequence}
                                editContext={this.props.editContext!}
                                flow={this.props.flow!}
                            />
                        </>
                    )}

                    {!this.props.sequence.isCollapsed &&
                        this.props.sequence.branches.map((b, index) => {
                            return (
                                <Box
                                    padding={"10px"}
                                    border={"1px"}
                                    borderColor={UIStyleConstants.GRAY_COLOR}
                                    bgcolor={UIStyleConstants.WHITE_DARK_COLOR}
                                    style={{ borderStyle: "solid" }}
                                    marginTop={"5px"}
                                    borderRadius="4pxpx"
                                    width={"100%"}
                                    paddingTop={"7px"}
                                    paddingBottom={"7px"}
                                >
                                    <RenderSequenceTitle sequence={this.props.sequence} />
                                    <div className="stack-actions">
                                        <div className="button-group">
                                            <RenderSequenceActions
                                                editContext={this.props.editContext}
                                                sequence={this.props.sequence}
                                            />
                                        </div>
                                    </div>
                                    <Droppable
                                        droppableId={this.props.sequence._id + "::" + index}
                                        direction={"horizontal"}
                                    >
                                        {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
                                            return (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    style={{
                                                        display:
                                                            snapshot.isDraggingOver &&
                                                            this.props.sequence.sequenceType ===
                                                                FlowStepSequenceType.BRANCH_MERGE
                                                                ? "flex"
                                                                : undefined,
                                                    }}
                                                >
                                                    <RenderBranchSequenceContent
                                                        sequence={this.props.sequence}
                                                        editContext={this.props.editContext}
                                                        flow={this.props.flow}
                                                        steps={b.steps}
                                                    />
                                                    <div
                                                        style={{
                                                            display: this.isEnabledPlaceholderWhileDrop(
                                                                this.props.sequence,
                                                            )
                                                                ? "block"
                                                                : "none",
                                                        }}
                                                    >
                                                        {provided.placeholder}
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    </Droppable>
                                    <ObserveState
                                        properties={[FlowStepSequence.STRATEGY]}
                                        listenTo={this.props.editContext}
                                        control={() => {
                                            return (
                                                <RenderSequenceStrategy
                                                    editContext={this.props.editContext}
                                                    sequence={this.props.sequence}
                                                />
                                            );
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    {!this.props.sequence.isCollapsed && (
                        <Box
                            display={"flex"}
                            justifyContent={"end"}
                        >
                            <Link
                                style={{ paddingTop: "5px", fontSize: "12px" }}
                                onClick={() => {
                                    this.props.sequence.addBranch();
                                }}
                            >
                                Add Branch
                            </Link>
                        </Box>
                    )}
                </Box>
            </FlowSequenceDiv>
        );
    }

    private RenderLoopBack() {
        let lineColor = UIStyleConstants.BLUE_COLOR;
        if (
            this.props.sequence.sequenceType === FlowStepSequenceType.ITERATE ||
            this.props.sequence.sequenceType === FlowStepSequenceType.ITERATE_WHILE ||
            this.props.sequence.sequenceType === FlowStepSequenceType.ITERATE_PARALLEL
        ) {
            let isWhile = this.props.sequence.sequenceType === FlowStepSequenceType.ITERATE_WHILE;
            let loopbackWidth = isWhile ? "23px" : "15px";
            return (
                <Box
                    marginRight={isWhile ? "12px" : "7px"}
                    marginLeft={isWhile ? "5px" : "0px"}
                    width={"5px"}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"space-between"}
                    borderLeft={"1px"}
                    borderRight={"0px"}
                    borderTop={"0px"}
                    borderBottom={"0px"}
                    borderColor={lineColor}
                    padding={"0px"}
                    style={{ borderStyle: "dashed" }}
                >
                    <Box height={"1px"}>
                        <Box
                            width={loopbackWidth}
                            height="11px"
                            position="absolute"
                            marginTop={"-13px"}
                            marginLeft={"-1px"}
                            borderLeft={"1px"}
                            borderRight={"1px"}
                            borderTop={"1px"}
                            borderBottom={"0px"}
                            borderColor={lineColor}
                            style={{
                                borderStyle: "solid",
                                borderTopLeftRadius: "7px",
                                borderTopRightRadius: "7px",
                            }}
                        ></Box>

                        <Box
                            width="15px"
                            height="11px"
                            position="absolute"
                            marginTop={"-14px"}
                            marginLeft={isWhile ? "18px" : "10px"}
                        >
                            <DrawArrowHead />
                        </Box>

                        {this.props.sequence.sequenceType === FlowStepSequenceType.ITERATE_PARALLEL && (
                            <>
                                <Box
                                    width="25px"
                                    height="11px"
                                    position="absolute"
                                    marginTop={"-13px"}
                                    marginLeft={"-1px"}
                                    borderLeft={"1px"}
                                    borderRight={"1px"}
                                    borderTop={"1px"}
                                    borderBottom={"0px"}
                                    borderColor={lineColor}
                                    style={{
                                        borderStyle: "solid",
                                        borderTopLeftRadius: "7px",
                                        borderTopRightRadius: "7px",
                                    }}
                                ></Box>

                                <Box
                                    width="15px"
                                    height="11px"
                                    position="absolute"
                                    marginTop={"-14px"}
                                    marginLeft={"20px"}
                                >
                                    <DrawArrowHead />
                                </Box>

                                <Box
                                    width="35px"
                                    height="11px"
                                    position="absolute"
                                    marginTop={"-13px"}
                                    marginLeft={"-1px"}
                                    borderLeft={"1px"}
                                    borderRight={"1px"}
                                    borderTop={"1px"}
                                    borderBottom={"0px"}
                                    borderColor={lineColor}
                                    style={{
                                        borderStyle: "solid",
                                        borderTopLeftRadius: "7px",
                                        borderTopRightRadius: "7px",
                                    }}
                                ></Box>
                                <Box
                                    width="15px"
                                    height="11px"
                                    position="absolute"
                                    marginTop={"-14px"}
                                    marginLeft={"30px"}
                                >
                                    <DrawArrowHead />
                                </Box>
                            </>
                        )}
                    </Box>

                    <Box height={"1px"}>
                        <Box
                            width={loopbackWidth}
                            height="17px"
                            position="absolute"
                            marginTop={"-1px"}
                            marginLeft={"-1px"}
                            borderLeft={"1px"}
                            borderRight={"1px"}
                            borderTop={"0px"}
                            borderBottom={"1px"}
                            borderColor={lineColor}
                            style={{
                                borderStyle: "solid",
                                borderBottomLeftRadius: "7px",
                                borderBottomRightRadius: "7px",
                            }}
                        ></Box>
                        {isWhile && (
                            <Box
                                width="25px"
                                height="25px"
                                border={"1px"}
                                style={{
                                    borderStyle: "solid",
                                    borderColor: UIStyleConstants.GRAY_LIGHTER_COLOR,
                                    borderRadius: "4px",
                                }}
                                bgcolor={"white"}
                                position="absolute"
                                marginTop={"-28px"}
                                marginLeft={"-14px"}
                                onClick={() => this.setState({ openExpression: true })}
                            >
                                <ExpressionIcon />
                            </Box>
                        )}
                        {this.props.sequence.sequenceType === FlowStepSequenceType.ITERATE_PARALLEL && (
                            <>
                                <Box
                                    width="25px"
                                    height="17px"
                                    position="absolute"
                                    marginTop={"-1px"}
                                    marginLeft={"-1px"}
                                    borderLeft={"1px"}
                                    borderRight={"1px"}
                                    borderTop={"0px"}
                                    borderBottom={"1px"}
                                    borderColor={lineColor}
                                    style={{
                                        borderStyle: "solid",
                                        borderBottomLeftRadius: "7px",
                                        borderBottomRightRadius: "7px",
                                    }}
                                ></Box>
                                <Box
                                    width="35px"
                                    height="17px"
                                    position="absolute"
                                    marginTop={"-1px"}
                                    marginLeft={"-1px"}
                                    borderLeft={"1px"}
                                    borderRight={"1px"}
                                    borderTop={"0px"}
                                    borderBottom={"1px"}
                                    borderColor={lineColor}
                                    style={{
                                        borderStyle: "solid",
                                        borderBottomLeftRadius: "7px",
                                        borderBottomRightRadius: "7px",
                                    }}
                                ></Box>
                            </>
                        )}
                    </Box>
                    {this.props.sequence.whileExpression && (
                        <ExpressionEditLayer
                            open={this.state.openExpression}
                            expression={this.props.sequence.whileExpression!}
                            onClose={() => this.setState({ openExpression: false })}
                        />
                    )}
                </Box>
            );
        }
    }
}

function DrawArrowHead() {
    return (
        <svg
            width="7"
            height="7"
            viewBox="-2.5 -5 75 60"
            preserveAspectRatio="none"
        >
            <path
                d="M0,0 l35,50 l35,-50"
                fill={UIStyleConstants.BLUE_COLOR}
                stroke="green"
                stroke-linecap="round"
                stroke-width="5"
            />
        </svg>
    );
}
